<template>

    <transition name="fade">
        <v-form-group
            v-if="loaded && items.length"
            :key="'related-' + reviewId"
            :label="$t('labels.related_reviews')"
            class="mb-0">
            <v-table bordered class="related-reviews" scrollable selectable small>
                <thead>
                    <th>{{ $t('labels.rating') }}</th>
                    <th>{{ $t('labels.source') }}</th>
                    <th>{{ $t('labels.review') }}</th>
                    <th v-if="showSimilarity">{{ $t('labels.related_similarity') }}</th>
                </thead>
                <tbody>
                    <tr v-for="r in items" :key="r.id" @click.stop="openRelatedInNewTab(r.id)">
                        <td>
                            <v-star-rating
                                v-if="r.rating"
                                :color="r.rating.color"
                                :score="r.rating.score"
                                :size="12" />
                            <span v-else class="text-muted">—</span>
                        </td>
                        <td>{{ r.source ? r.source.name : $t('labels.none') }}</td>
                        <td class="related-reviews__excerpt">{{ r.excerpt }}</td>
                        <td v-if="showSimilarity" class="related-reviews__similarity">
                            <div v-if="r.similarity != null" class="related-reviews__meter-wrap text-primary">
                                <div
                                    class="related-reviews__meter"
                                    role="progressbar"
                                    :aria-valuenow="similarityPercent(r.similarity)"
                                    :aria-valuetext="similarityPercent(r.similarity) + '% similar'"
                                    aria-valuemin="0"
                                    aria-valuemax="100">
                                    <div
                                        class="related-reviews__meter-fill"
                                        :style="{ width: similarityMeterWidth(r.similarity) }" />
                                </div>
                            </div>
                            <span v-else class="text-muted">—</span>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </v-form-group>
    </transition>

</template>

<style scoped lang="scss">

    .related-reviews__excerpt {
        max-width: 36vw;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .related-reviews__similarity {
        vertical-align: middle;
    }

    .related-reviews__meter-wrap {
        display: inline-block;
        vertical-align: middle;
        width: 48px;
    }

    .related-reviews__meter {
        background: $color-bg;
        border-radius: 9999px;
        height: 6px;
        overflow: hidden;
        width: 100%;
    }

    .related-reviews__meter-fill {
        background: currentColor;
        border-radius: 9999px;
        height: 100%;
        max-width: 100%;
        min-width: 0;
        transition: width 0.2s ease;
    }

</style>

<script>

import ReviewApi from '@api/ReviewApi';
import StarRating from '@cmp/StarRating';

export default {

    components: {
        'v-star-rating': StarRating,
    },

    props: {
        reviewId: {
            type: Number,
            required: true,
        },
        showSimilarity: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            items: [],
            loaded: false,
        }
    },

    watch: {
        reviewId: {
            handler() {
                this.fetchRelated();
            },
            immediate: true,
        },
    },

    methods: {

        fetchRelated() {
            this.items = [];
            this.loaded = false;
            if (!this.reviewId) {
                this.loaded = true;

                return;
            }

            ReviewApi.getRelated(this.reviewId)
                .then((response) => {
                    this.items = response.data.data || [];
                })
                .catch(() => {})
                .finally(() => {
                    this.loaded = true;
                });
        },

        openRelatedInNewTab(id) {
            const route = this.$router.resolve({ name: 'reviews.show', params: { id: String(id) } });
            window.open(route.href, '_blank', 'noopener,noreferrer');
        },

        similarityMeterWidth(similarity) {
            return `${this.similarityPercent(similarity)}%`;
        },

        similarityPercent(similarity) {
            const n = Number(similarity);
            if (Number.isNaN(n)) {
                return 0;
            }

            return Math.max(0, Math.min(100, Math.round(n * 100)));
        },

    },

}

</script>
